import { useCallback, useEffect, useRef, useState } from "react";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import Drawer from "../../../../../../../components/SideDrawer/SideDrawer";
import ViewingsIcon from "../../../../../../../assets/svg/viewings-icon";
import CloseIcon from "../../../../../../../assets/svg/close-icon";
import CheckIcon from "../../../../../../../assets/svg/check-icon";
import ClockIcon from "../../../../../../../assets/svg/clock-icon";
import SearchableDropdown from "../../../../../../../components/SearchableDropdown/SearchableDropdown";
import SingleDatePicker from "../../../../../../../components/SingleDatePicker/SingleDatePicker";
import CalendarIcon from "../../../../../../../assets/svg/calendar-icon";
import TimePicker from "../../../../../../../components/TimePicker/TimePicker";
import Dropdown from "../../../../../../../components/Dropdown/Dropdown";
import Input, { InputLabel } from "../../../../../../../components/Input/Input";
import SearchBar from "../../../../../../../components/SearchBar/SearchBar";
import { SuggestionItem } from "../../../../../../../components/SearchBar/types";
import { Avatar } from "../../../../../../../components/Avatar/Avatar";
import LeadTypeBadge from "../../../../../../../components/LeadCard/LeadTypeBadge";
import { TruncatedTextTooltip } from "../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
// import AgentAssignmentDropdown from "../../../../components/AgentAssignmentDropdown";
import Button from "../../../../../../../components/Button/Button";
import {
  useCreateViewingMutation,
  useGetLeadPropertiesListQuery,
  useGetCalendarSyncStatusQuery,
  useGetLeadCreateDefaultsQuery,
} from "../../../../../../../redux/services/crmApi";
import { getClientCRMFlag } from "../../../../../../../utils/constants/clientCustomCRMFlags";
import { useLazyGetLeadListQuery } from "../../../../../../../redux/services/communicationApi";
import { Lead } from "../../../../../utils/types";
import { useGetPropertyDetailsQuery } from "../../../../../../../redux/services/pmsApi";
import { RootState } from "../../../../../../../redux/store";
import { DropdownOption } from "../../../../../../../components/Dropdown/types";
import NetworkIcon from "../../../../../../../assets/svg/network-icon";
import LinkIcon from "../../../../../../../assets/svg/link-icon";
//import { viewingTimeZone } from "../utils/constants";
import { timezoneOptions } from "../../../../../../../utils/constants/timezoneOptions";
import { meetingPlatformOptions } from "../utils/constants";
import { customToast } from "../../../../../../../utils/custom-toast/custom-toast";
import AgentDropdown from "../../../../../../../components/AgentDropdown/AgentDropdown";
import TextArea from "../../../../../../../components/TextArea/TextArea";
import { formatAsLocalTime } from "../../../../../../../utils/functions/date-time";
import { getViewingLabels } from "../../../../../../../utils/constants/mappings";

interface ExtendedDropdownOption extends DropdownOption {
  label: string;
}

interface Property {
  id: string;
  name: string;
}

type LeadSuggestionItem = SuggestionItem & {
  lead?: { name: string; email: string };
};

interface ViewingFormValues {
  name: string;
  email: string;
  viewing_type: "virtual" | "physical";
  client_id: string;
  assignees: string[];
  event_name: string;
  lead_id: string;
  physical_meet_details: {
    location: string;
  };
  property_details: {
    chosen_properties: Array<{
      property_id: number;
      property_name: string;
    }>;
    room_type: string;
  };
  virtual_meet_details: {
    platform: string;
    joining_link: string;
  };
  date: string;
  time: string;
  timezone: string;
  notes: string;
  sync_to_calendar: boolean;
}

const getValidationSchema = (integrationStatus: boolean, showLeadId: boolean) =>
  Yup.object().shape({
    event_name: Yup.string().required("Event name is required"),
    timezone: Yup.string().required("Timezone is required"),
    date: Yup.string().required("Schedule date is required"),
    time: Yup.string().required("Time is required"),
    physical_meet_details: Yup.object().shape({
      location: Yup.string().required("Location is required"),
    }),
    assignees: Yup.array().min(1, "At least one agent must be selected"),
    lead_id: showLeadId
      ? Yup.string().required("Lead ID is required")
      : Yup.string(),
    name: showLeadId
      ? Yup.string().required(
          "Please select a different lead, this lead doesn't have a name"
        )
      : Yup.string(),
    email: showLeadId
      ? Yup.string()
          .email(
            "Please select a different lead, this lead doesn't have an email"
          )
          .required(
            "Please select a different lead, this lead doesn't have an email"
          )
      : Yup.string(),
    virtual_meet_details: Yup.object().when("viewing_type", {
      is: "virtual",
      then: (schema) =>
        schema.shape({
          platform: Yup.string().required("Platform is required"),
          joining_link: integrationStatus
            ? Yup.string()
            : Yup.string().required("Joining link is required"),
        }),
      otherwise: (schema) => schema.strip(),
    }),
  });

const ChosenPropertyAutoSelect = ({
  open,
  options,
  hasValue,
  preferredValue,
  waitForPreferred,
  onAutoSelect,
}: {
  open: boolean;
  options: ExtendedDropdownOption[];
  hasValue: boolean;
  // The client's configured default property, selected when it is one of `options`.
  preferredValue?: string;
  // True while that default is still loading — hold off so the single-option
  // fallback below can't win the race.
  waitForPreferred?: boolean;
  onAutoSelect: (option: ExtendedDropdownOption) => void;
}) => {
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  useEffect(() => {
    if (!open) {
      setHasAutoSelected(false);
      return;
    }
    if (hasAutoSelected) return;
    if (hasValue) {
      setHasAutoSelected(true);
      return;
    }
    if (waitForPreferred) return;

    const preferredOption = preferredValue
      ? options?.find((option) => option?.value === preferredValue)
      : undefined;
    if (preferredOption) {
      setHasAutoSelected(true);
      onAutoSelect(preferredOption);
      return;
    }

    if (options.length !== 1) return;
    setHasAutoSelected(true);
    onAutoSelect(options[0]);
  }, [
    open,
    hasAutoSelected,
    hasValue,
    options,
    preferredValue,
    waitForPreferred,
    onAutoSelect,
  ]);

  return null;
};

const AddViewingDrawer = ({
  open,
  onClose,
  viewingType,
  setViewingType,
  leadId,
  leadName,
  leadEmail,
  showLeadId = false,
}: {
  open: boolean;
  onClose: () => void;
  viewingType: "on-site" | "virtual";
  setViewingType: (viewingType: "on-site" | "virtual") => void;
  leadId: string;
  leadName: string;
  leadEmail: string;
  showLeadId?: boolean;
}) => {
  const managerId = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const [propertiesOptions, setPropertiesOptions] = useState<
    ExtendedDropdownOption[]
  >([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const agentsRef = useRef<HTMLDivElement>(null);

  const [getLeadList] = useLazyGetLeadListQuery();

  const handleLeadSearch = useCallback(
    async (query: string): Promise<SuggestionItem[]> => {
      const trimmed = query?.trim();
      if (!trimmed) return [];
      try {
        const result = await getLeadList({
          search_query: trimmed,
          chatType: "leads_created",
          is_crm: true,
        })?.unwrap();
        if (!result?.lead_cards) return [];
        return result?.lead_cards?.map((lead: Lead): LeadSuggestionItem => {
          const id = String(lead?.lead_id ?? "");
          return {
            id,
            value: id,
            lead: {
              name: lead?.name ?? "",
              email: lead?.email ?? "",
            },
            content: (
              <div className="p-1 flex items-center justify-between gap-[8px]">
                <div className="flex items-center gap-[5px] min-w-0">
                  <Avatar text={lead?.name || "Anonymous"} size={24} />
                  <TruncatedTextTooltip
                    text={lead?.name || "Anonymous"}
                    maxWidth="max-w-[160px]"
                    className="text-data dark:text-data-dk font-medium text-md"
                  />
                </div>
                <div className="flex items-center gap-[8px]">
                  <LeadTypeBadge originalLeadId={lead?.original_lead_id} />
                  <p className="text-md text-dark-gray dark:text-dark-gray-dk font-normal">
                    #{id}
                  </p>
                </div>
              </div>
            ),
          };
        });
      } catch {
        return [];
      }
    },
    [getLeadList]
  );

  const { data: propertiesList } = useGetLeadPropertiesListQuery({
    managerId: managerId || "",
    leadId: leadId || undefined,
  });

  const { data: propertyDetails, isFetching: isRoomTypesFetching } =
    useGetPropertyDetailsQuery(selectedPropertyId, {
      skip: !selectedPropertyId,
    });

  const roomTypeOptions = (propertyDetails?.room_kinds || []).map(
    (roomKind: { value: string; label: string }) => ({
      value: roomKind.value.toLowerCase(),
      label: roomKind.label,
    })
  );

  const client = useSelector((state: RootState) => state?.auth?.client);
  const outlookIntegration = useSelector(
    (state: RootState) => state?.auth?.outlookIntegration
  );
  const integrationStatus = outlookIntegration?.integration_status ?? false;
  const isVerbafloClient = client?.client_code === "verbaflo";
  const viewingLabels = getViewingLabels(client?.region, isVerbafloClient);

  // Only clients with configured defaults (aparto) prefill the chosen property
  // and location details from /dashboard/leads/create/defaults.
  const hasLeadCreateDefaults = getClientCRMFlag(
    "hasLeadCreateDefaults",
    client?.client_code
  );
  const { data: clientDefaults, isLoading: isClientDefaultsLoading } =
    useGetLeadCreateDefaultsQuery(undefined, {
      skip: !open || !hasLeadCreateDefaults,
    });
  const defaultPropertyValue = clientDefaults?.property_id
    ? String(clientDefaults?.property_id)
    : undefined;

  const { data: calendarSyncData } = useGetCalendarSyncStatusQuery(undefined, {
    skip: viewingType !== "virtual",
    refetchOnMountOrArgChange: true,
  });
  const isCalendarSyncAvailable =
    calendarSyncData?.is_calendar_sync_available ?? false;

  const [createViewing] = useCreateViewingMutation();

  useEffect(() => {
    if (propertiesList?.res) {
      setPropertiesOptions(
        propertiesList?.res?.map((property: Property) => ({
          value: String(property?.id),
          label: property?.name,
          content: (
            <span className="text-md text-heading-text dark:text-heading-text-dk">
              {property?.name}
            </span>
          ),
        }))
      );
    }
  }, [propertiesList]);

  useEffect(() => {
    if (!open) {
      setSelectedAgentIds([]);
      setSelectedPropertyId("");
    }
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      direction="right"
      size="471px"
      overlayColor="rgba(0,0,0,0.7)"
      duration={300}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      drawerContainerClassName="rounded-l-[8px]"
    >
      {open && (
        <Formik<ViewingFormValues>
          initialValues={{
            name: leadName,
            email: leadEmail,
            viewing_type: viewingType === "virtual" ? "virtual" : "physical",
            client_id: managerId || "",
            assignees: [],
            event_name: "",
            lead_id: leadId,
            physical_meet_details: {
              location: "",
            },
            property_details: {
              chosen_properties: [],
              room_type: "",
            },
            virtual_meet_details: {
              platform: "teams",
              joining_link: "",
            },
            date: "",
            time: "",
            timezone: "",
            notes: "",
            sync_to_calendar: true,
          }}
          validationSchema={getValidationSchema(integrationStatus, showLeadId)}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            const { date, time, ...restValues } = values;
            const scheduled_at = new Date(`${date}T${time}`)?.toISOString();

            const {
              virtual_meet_details,
              notes,
              sync_to_calendar,
              ...rest
            } = restValues;
            const payload = {
              ...rest,
              scheduled_at,
              ...(notes?.trim()
                ? { notes: notes.trim().replace(/\n/g, "<br>") }
                : {}),
              ...(rest.viewing_type === "virtual"
                ? {
                    virtual_meet_details: {
                      meeting_platform: virtual_meet_details?.platform || "",
                      joining_link: integrationStatus
                        ? ""
                        : virtual_meet_details?.joining_link || "",
                    },
                    sync_to_calendar:
                      isCalendarSyncAvailable && sync_to_calendar,
                  }
                : {}),
            };
            try {
              await createViewing(payload).unwrap();
              customToast(
                `${viewingLabels?.singular} created successfully`,
                "success"
              );
              resetForm();
              onClose();
            } catch (error) {
              console.error("Error creating viewing:", error);
              customToast(
                "Error creating viewing",
                "error",
                undefined,
                undefined,
                "bottom-left"
              );
            } finally {
              setSubmitting(false);
              setSelectedAgentIds([]);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            submitCount,
            handleChange,
            setFieldValue,
            setFieldTouched,
            setValues,
            isSubmitting,
            submitForm,
          }) => {
            const shouldShowError = (fieldPath: string) =>
              (getIn(touched, fieldPath) || submitCount > 0) &&
              Boolean(getIn(errors, fieldPath));

            const getErrorMessage = (fieldPath: string) =>
              shouldShowError(fieldPath)
                ? (getIn(errors, fieldPath) as string)
                : "";

            const timezoneError = getErrorMessage("timezone");
            const dateError = getErrorMessage("date");
            const timeError = getErrorMessage("time");
            const chosenPropertyError = getErrorMessage(
              "property_details.chosen_properties"
            );
            const roomTypeError = getErrorMessage("property_details.room_type");
            const joiningLinkError = getErrorMessage(
              "virtual_meet_details.joining_link"
            );
            const locationError = getErrorMessage(
              "physical_meet_details.location"
            );
            const eventNameError = getErrorMessage("event_name");
            const assigneesError = getErrorMessage("assignees");
            const leadIdError = getErrorMessage("lead_id");
            const leadNameError = getErrorMessage("name");
            const leadEmailError = getErrorMessage("email");
            const leadDetailsError =
              leadIdError || leadNameError || leadEmailError;

            const handleLeadSelect = (
              selectedLeadId: string,
              item?: SuggestionItem
            ) => {
              const pickedLead = (item as LeadSuggestionItem | undefined)?.lead;
              setValues(
                {
                  ...values,
                  lead_id: selectedLeadId,
                  name: pickedLead?.name ?? "",
                  email: pickedLead?.email ?? "",
                },
                true
              );
              setFieldTouched("lead_id", true, false);
              setFieldTouched("name", true, false);
              setFieldTouched("email", true, false);
            };

            const handleNotesChange = (
              e: React.ChangeEvent<HTMLTextAreaElement>
            ) => {
              if (e.target.value.length > 250) {
                e.target.value = e.target.value.slice(0, 250);
              }
              handleChange(e);
            };

            const handleLeadClear = () => {
              setValues(
                {
                  ...values,
                  lead_id: "",
                  name: "",
                  email: "",
                },
                true
              );
            };

            const handleChosenPropertyAutoSelect = (
              option: ExtendedDropdownOption
            ) => {
              setFieldValue("property_details.chosen_properties", [
                {
                  property_id: parseInt(option?.value, 10),
                  property_name: option?.label || "",
                },
              ]);
              setSelectedPropertyId(option?.value);
              setFieldValue("property_details.room_type", "");

              // Only the client-default property also fills the location, and
              // never over a location the user already typed.
              if (
                option?.value === defaultPropertyValue &&
                !values?.physical_meet_details?.location?.trim()
              ) {
                // "Binary Hub, Dublin, Ireland" — nulls from the API drop out.
                const defaultLocation = [
                  clientDefaults?.property_name || option?.label,
                  clientDefaults?.city_name,
                  clientDefaults?.country_name,
                ]
                  ?.filter(Boolean)
                  ?.join(", ");
                setFieldValue("physical_meet_details.location", defaultLocation);
              }
            };

            return (
              <>
                <ChosenPropertyAutoSelect
                  open={open}
                  options={propertiesOptions}
                  hasValue={Boolean(
                    values?.property_details?.chosen_properties?.[0]
                      ?.property_id
                  )}
                  preferredValue={defaultPropertyValue}
                  waitForPreferred={
                    hasLeadCreateDefaults && isClientDefaultsLoading
                  }
                  onAutoSelect={handleChosenPropertyAutoSelect}
                />
              <Form className="flex flex-col gap-6 py-3 px-4.5 h-full no-autofill-style min-h-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <ViewingsIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
                    <h2 className="flex gap-1 items-center text-base  text-heading-text dark:text-heading-text-dk">
                      {viewingLabels.addAction}
                    </h2>
                  </div>
                  <button
                    className="w-[16px] h-[16px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex flex-col items-center justify-center"
                    aria-label="close create user modal"
                    onClick={onClose}
                    type="button"
                  >
                    <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
                  </button>
                </div>
                <div className="flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto no-scrollbar pr-1">
                  {showLeadId && (
                    <div className="flex flex-col gap-1.5">
                      <span className="text-md text-verba-field-label dark:text-verba-field-label-dk">
                        Lead ID
                      </span>
                      <SearchBar
                        placeholder="Search by Lead ID or Name"
                        defaultValue={values?.lead_id}
                        enableApiSearch
                        onApiSearch={handleLeadSearch}
                        onSelect={handleLeadSelect}
                        onClear={handleLeadClear}
                        apiSearchDebounceMs={300}
                        minCharsToSearch={1}
                        showSearchIcon={false}
                        showSuggestionDropdown
                        suggestionsClassName="max-h-[200px]"
                        containerClassName="w-[262px]"
                        className="h-[32px]! py-0! px-[8px]! rounded-lg border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk text-md font-normal text-heading-text dark:text-heading-text-dk"
                        noSuggestionsText="No leads found"
                      />
                      {leadDetailsError && (
                        <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk">
                          {leadDetailsError}
                        </span>
                      )}
                    </div>
                  )}
                  {!isVerbafloClient && (
                    <div className="flex flex-col gap-1.5">
                      <h3 className="text-md text-verba-field-label dark:text-verba-field-label-dk">
                        {viewingLabels.type}
                      </h3>
                      <div className="flex items-center gap-2 cursor-pointer">
                        <div
                          onClick={() => {
                            setViewingType("on-site");
                            setFieldValue("viewing_type", "physical");
                          }}
                          className={`flex flex-col gap-2 border rounded-lg p-3
                ${
                  viewingType === "on-site"
                    ? "border-verba-blue dark:border-verba-blue-dk border-2 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
                    : "border-border-gray dark:border-border-gray-dk"
                }`}
                        >
                          <div className="flex items-center gap-1">
                            <span
                              className={`w-[10px] h-[10px] border rounded-full flex items-center justify-center
                ${
                  viewingType === "on-site"
                    ? "border-verba-blue dark:border-verba-blue-dk border-2 bg-verba-blue dark:bg-verba-blue-dk"
                    : "border-gray-placeholder dark:border-gray-placeholder-dk"
                }`}
                            >
                              {viewingType === "on-site" && (
                                <CheckIcon className="w-[10px] h-[10px] text-pure-white dark:text-pure-white-dk" />
                              )}
                            </span>

                            <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk font-semibold">
                              On-Site {viewingLabels.singular}
                            </span>
                          </div>
                          <p className="text-xs text-data dark:text-data-dk pr-4">
                            Come take a look in person and step <br /> inside
                            your future student home.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            onClick={() => {
                              setViewingType("virtual");
                              setFieldValue("viewing_type", "virtual");
                            }}
                            className={`flex flex-col gap-2 border rounded-lg p-3
                ${
                  viewingType === "virtual"
                    ? "border-verba-blue dark:border-verba-blue-dk  border-2 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
                    : "border-border-gray dark:border-border-gray-dk"
                }`}
                          >
                            <div className="flex items-center gap-1">
                              <span
                                className={`w-[10px] h-[10px] border rounded-full flex items-center justify-center
                ${
                  viewingType === "virtual"
                    ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk"
                    : "border-gray-placeholder dark:border-gray-placeholder-dk"
                }`}
                              >
                                {viewingType === "virtual" && (
                                  <CheckIcon className="w-[10px] h-[10px] text-pure-white dark:text-pure-white-dk" />
                                )}
                              </span>
                              <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk font-semibold">
                                Virtual {viewingLabels.singular}
                              </span>
                            </div>
                            <p className="text-xs text-data dark:text-data-dk pr-4">
                              Say hello online! Our team is ready <br /> to show
                              you around via video.
                            </p>
                          </div>
                        </div>
                      </div>
                      <span className="text-xxs text-gray-placeholder dark:text-gray-placeholder-dk flex items-center gap-1">
                        <ClockIcon className="w-[12px] h-[12px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                        Duration: 30 minutes
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col gap-3.5">
                    <div className="flex flex-col gap-2">
                      <SearchableDropdown
                        options={timezoneOptions}
                        value={values?.timezone}
                        onChange={(value) => {
                          setFieldValue("timezone", value);
                          setFieldTouched("timezone", true, false);
                        }}
                        placeholder="Select a timezone"
                        sectionHeading="All Timezones"
                        selectedOptionContainerClassName="w-full"
                        placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                        className="w-[264px]"
                        Icon={NetworkIcon}
                        iconClassName="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"
                        triggerClassName="w-full bg-pure-white dark:bg-pure-white-dk"
                        label="Timezone"
                        labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                      />
                      {values?.timezone && values?.date && values?.time && (
                        <span className="text-xxs text-dark-gray dark:text-dark-gray-dk">
                          <span className="font-semibold text-verba-field-label dark:text-verba-field-label-dk">
                            Local time:
                          </span>{" "}
                          {`${formatAsLocalTime(
                            values.date,
                            values.time,
                            values.timezone
                          )} (${
                            Intl.DateTimeFormat()?.resolvedOptions()?.timeZone
                          })`}
                        </span>
                      )}
                      {timezoneError && (
                        <span className="text-xs text-red-500 ">
                          {timezoneError}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <InputLabel
                        label={viewingLabels.schedule}
                        labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk whitespace-nowrap"
                      />
                      <div className="flex items-center gap-2">
                        <SingleDatePicker
                          value={values?.date}
                          Icon={CalendarIcon}
                          onChange={(date) => {
                            if (date) {
                              const newDate = new Date(date);
                              const year = newDate.getFullYear();
                              const month = String(
                                newDate.getMonth() + 1
                              ).padStart(2, "0");
                              const day = String(newDate.getDate()).padStart(
                                2,
                                "0"
                              );
                              setFieldValue("date", `${year}-${month}-${day}`);
                              setFieldTouched("date", true, false);
                            }
                          }}
                          placeholder="Select date"
                          containerClassName="w-[129px]"
                          inputClassName="border border-border-gray dark:border-border-gray-dk rounded-lg p-2 text-md font-normal text-heading-text dark:text-heading-text-dk"
                        />

                        <TimePicker
                          value={values?.time}
                          onChange={(time) => {
                            setFieldValue("time", time);
                            setFieldTouched("time", true, false);
                          }}
                          placeholder="Select time"
                          className="w-[129px] h-full!"
                          Icon={ClockIcon}
                          iconClassName="w-3 h-3 text-gray-placeholder dark:text-gray-placeholder-dk"
                          triggerIconClassName="bg-pure-white dark:bg-pure-white-dk"
                          selectedOptionContainerClassName="w-full h-[36px] py-0 !gap-0 !px-3"
                          placeholderClassName="text-md font-normal placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
                          optionClassName="text-md font-normal text-heading-text dark:text-heading-text-dk border-none"
                          triggerClassName="h-full"
                        />
                      </div>
                      {(dateError || timeError) && (
                        <span className="text-xs text-red-500 ">
                          {timeError || dateError}
                        </span>
                      )}
                    </div>

                    {viewingType === "virtual" && isCalendarSyncAvailable && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={values?.sync_to_calendar}
                          onChange={() =>
                            setFieldValue(
                              "sync_to_calendar",
                              !values?.sync_to_calendar
                            )
                          }
                        />
                        <span
                          className={`flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[4px] transition-colors ${
                            values?.sync_to_calendar
                              ? "bg-verba-blue dark:bg-verba-blue-dk"
                              : "border border-gray-placeholder dark:border-gray-placeholder-dk bg-pure-white dark:bg-pure-white-dk"
                          }`}
                        >
                          {values?.sync_to_calendar && (
                            <CheckIcon className="w-2 h-2 text-pure-white dark:text-pure-white-dk" />
                          )}
                        </span>
                        <span className="text-md text-heading-text dark:text-heading-text-dk">
                          Sync to Calendar (Calendly)
                        </span>
                      </label>
                    )}

                    <div>
                      <Dropdown
                        options={propertiesOptions}
                        value={
                          values?.property_details?.chosen_properties[0]
                            ?.property_id
                            ? String(
                                values?.property_details?.chosen_properties[0]
                                  ?.property_id
                              )
                            : ""
                        }
                        onChange={(value) => {
                          const selectedProperty = propertiesOptions?.find(
                            (opt) => opt?.value === value
                          );
                          setFieldValue("property_details.chosen_properties", [
                            {
                              property_id: parseInt(value, 10),
                              property_name: selectedProperty?.label || "",
                            },
                          ]);
                          setFieldTouched(
                            "property_details.chosen_properties",
                            true,
                            false
                          );
                          setSelectedPropertyId(value);
                          setFieldValue("property_details.room_type", "");
                        }}
                        placeholder="Select property"
                        placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                        selectedOptionContainerClassName="w-full"
                        className="w-[264px]"
                        triggerClassName="w-full bg-pure-white dark:bg-pure-white-dk"
                        label="Chosen Property"
                        labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                      />
                      {chosenPropertyError && (
                        <span className="text-xs text-red-500 mt-1">
                          {chosenPropertyError}
                        </span>
                      )}
                    </div>

                    <div>
                      <Dropdown
                        options={roomTypeOptions?.map(
                          (option: { value: string; label: string }) => ({
                            value: option?.value,
                            label: option?.label,
                            content: (
                              <span className="text-md text-heading-text dark:text-heading-text-dk">
                                {option?.label}
                              </span>
                            ),
                          })
                        )}
                        value={values?.property_details?.room_type}
                        onChange={(value) => {
                          setFieldValue("property_details.room_type", value);
                          setFieldTouched(
                            "property_details.room_type",
                            true,
                            false
                          );
                        }}
                        disabled={!selectedPropertyId || isRoomTypesFetching}
                        placeholder={
                          !selectedPropertyId
                            ? "Select a property first"
                            : isRoomTypesFetching
                              ? "Loading room types..."
                              : "Select room type"
                        }
                        placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                        className="w-[264px]"
                        selectedOptionContainerClassName="w-full"
                        triggerClassName="w-full bg-pure-white dark:bg-pure-white-dk"
                        label="Room Type"
                        labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                      />
                      {roomTypeError && (
                        <span className="text-xs text-red-500 mt-1">
                          {roomTypeError}
                        </span>
                      )}
                    </div>

                    {viewingType === "virtual" && (
                      <div>
                        <Dropdown
                          options={meetingPlatformOptions}
                          value={values?.virtual_meet_details?.platform || ""}
                          onChange={(value) => {
                            setFieldValue(
                              "virtual_meet_details.platform",
                              value
                            );
                            setFieldTouched(
                              "virtual_meet_details.platform",
                              true,
                              false
                            );
                          }}
                          placeholder="Select meeting platform"
                          placeholderClassName="text-gray-placeholder dark:text-gray-placeholder-dk text-md font-normal"
                          selectedOptionContainerClassName="w-full"
                          className="w-[264px]"
                          triggerClassName="w-full bg-pure-white dark:bg-pure-white-dk"
                          label="Meeting Platform"
                          labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                        />
                      </div>
                    )}

                    {viewingType === "virtual" && !integrationStatus && (
                      <div className="flex flex-col gap-1">
                        <Input
                          name="virtual_meet_details.joining_link"
                          value={
                            values?.virtual_meet_details?.joining_link || ""
                          }
                          onChange={(event) => {
                            handleChange(event);
                            setFieldTouched(
                              "virtual_meet_details.joining_link",
                              true,
                              false
                            );
                          }}
                          placeholder="Enter link"
                          containerClassName="w-[264px]"
                          leftIcon={LinkIcon}
                          iconClassName="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk"
                          label="Joining Link"
                          labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                          secondaryContainerClassName="border border-border-gray dark:border-border-gray-dk rounded-lg p-2 text-md font-normal text-heading-text dark:text-heading-text-dk"
                        />
                        {joiningLinkError && (
                          <span className="text-xs text-red-500 mt-1">
                            {joiningLinkError}
                          </span>
                        )}
                      </div>
                    )}

                    <div>
                      <Input
                        name="physical_meet_details.location"
                        value={values?.physical_meet_details?.location}
                        onChange={(event) => {
                          handleChange(event);
                          setFieldTouched(
                            "physical_meet_details.location",
                            true,
                            false
                          );
                        }}
                        placeholder="Enter location details"
                        containerClassName="w-[264px]"
                        label="Location Details"
                        labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                        secondaryContainerClassName="border border-border-gray dark:border-border-gray-dk rounded-lg p-2 text-md font-normal text-heading-text dark:text-heading-text-dk"
                      />
                      {locationError && (
                        <span className="text-xs text-red-500 mt-1">
                          {locationError}
                        </span>
                      )}
                    </div>

                    <div>
                      <Input
                        name="event_name"
                        value={values?.event_name}
                        onChange={(event) => {
                          handleChange(event);
                          setFieldTouched("event_name", true, false);
                        }}
                        placeholder="Enter event name"
                        containerClassName="w-[264px]"
                        label="Event Name"
                        labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                        secondaryContainerClassName="border border-border-gray dark:border-border-gray-dk rounded-lg p-2 text-md font-normal text-heading-text dark:text-heading-text-dk"
                      />
                      {eventNameError && (
                        <span className="text-xs text-red-500 mt-1">
                          {eventNameError}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      <TextArea
                        name="notes"
                        value={values?.notes}
                        onChange={handleNotesChange}
                        placeholder="Briefly add more details about the viewings or any links you'd like..."
                        rows={3}
                        label={`${viewingLabels.singular} Description`}
                        labelClassName="text-md text-verba-field-label dark:text-verba-field-label-dk"
                        containerClassName="w-full"
                        secondaryContainerClassName="border border-border-gray dark:border-border-gray-dk rounded-lg p-2"
                      />
                      <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk text-right">
                        {values?.notes?.length || 0}/250 characters
                      </span>
                    </div>

                    <div ref={agentsRef} className="flex flex-col gap-1.5">
                      <h2 className="text-md text-verba-field-label dark:text-verba-field-label-dk">
                        Assigned to Agents(s)
                      </h2>
                      <span className="text-xs text-dark-gray dark:text-dark-gray-dk">
                        Assign this viewing to multiple agents.
                      </span>
                      {/* <AgentAssignmentDropdown
                  selectedAgentIds={selectedAgentIds}
                  onApply={(agentIds, selectedUsers) => {
                    const agentEmails = selectedUsers
                      .map((user) => user?.email || "")
                      .filter(Boolean);
                    setFieldValue("assignees", [...new Set([...agentIds])]);
                    setSelectedAgentIds(agentIds);
                    return Promise.resolve();
                  }}
                  maxAgents={4}
                  isViewing={true}
                  onToggleScroll={() => {}}
                /> */}
                      <AgentDropdown
                        selectedAgentIds={selectedAgentIds}
                        onSelect={(agentIds) => {
                          setFieldValue("assignees", agentIds);
                          setSelectedAgentIds(agentIds);
                          setFieldTouched("assignees", true, false);
                        }}
                        maxAgents={4}
                        className="min-w-[264px]! w-fit"
                      />
                      {assigneesError && (
                        <span className="text-xs text-red-500 mt-1">
                          {assigneesError}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    innerText="Cancel"
                    onClick={onClose}
                    buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
                    innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
                  />
                  <Button
                    type="button"
                    loaderColor="white"
                    innerText="Save"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={() => {
                      const singular =
                        viewingLabels?.singular?.toLowerCase() || "viewing";
                      if (!showLeadId) {
                        if (!values?.name?.trim()) {
                          customToast(
                            "Lead is missing a name",
                            "warning",
                            undefined,
                            `Add a name to this lead before scheduling a ${singular}`,
                            "bottom-left"
                          );
                          return;
                        }
                        if (!values?.email?.trim()) {
                          customToast(
                            "Lead is missing an email",
                            "warning",
                            undefined,
                            `Add an email to this lead before scheduling a ${singular}`,
                            "bottom-left"
                          );
                          return;
                        }
                      }
                      if (values?.assignees?.length === 0) {
                        agentsRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                      }
                      submitForm();
                    }}
                    buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue-hover dark:hover:bg-verba-blue-hover-dk"
                    innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
                  />
                </div>
              </Form>
              </>
            );
          }}
        </Formik>
      )}
    </Drawer>
  );
};

export default AddViewingDrawer;
